import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import PlayerMatchup from './PlayerMatchupPattern';
import { PlayerMatchupTypes, PlayerMatchupFormats } from '@mlb/react-patterns/v4/components/PlayerMatchup/playerMatchup.types';
import { statsMapsDuel, probablePitchersStatsMap } from '../utils/statsMapsDuel';
import ProbablePitcherInfo from './ProbablePitcherInfo';
import buildPlayerStatsDuel from '../utils/buildPlayerStatsDuel';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import { getLanguage } from '@gameday/utils/selectors/getLanguage';
import { useSelector } from 'react-redux';
import useTracking from '@gameday/utils/hooks/useTracking';
import usePrevious from '@gameday/utils/hooks/usePrevious';
import PlayerMatchupWrapper from './PlayerMatchupWrapper';

let basePreviewProps = {
    type: PlayerMatchupTypes.PITCHERPREGAME,
    stacked: true,
    format: PlayerMatchupFormats.VISUAL,
    headshotWidth: '96px',
    headshotZoomScale: 1,
    headshotRatio: '1:1',
    $gameState: 'preview'
};

const desiredHittingStats = {
    before: ['hits'],
    after: ['homeRuns', 'triples', 'doubles', 'baseOnBalls', 'hitByPitch', 'strikeOuts', 'rbi', 'runs', 'stolenBases', 'caughtStealing']
};

const desiredStats = {
    before: ['wins'],
    after: ['era'],
    bottom: ['pitcherK']
};

const condensedPitcherStats = {
    after: ['wins', 'era'],
    afterSeparator: ' | '
};

// Function to compare current and previous player.
const playerMatcher = (prev, current) => prev?.person?.id === current?.person?.id;
const noNameLabel = 'TBD';
const MatchupsList = ({
    probablePitchers,
    abstractGameCode,
    batter,
    pitcher,
    gameStateOverride,
    stacked,
    deviceInteraction,
    usePreviousPlayers,
    viewType,
    isMobile,
    isFullNarrow,
    headshotWidth,
    showCustomPlayerInfo = true
}) => {
    const condensedDisplay = viewType === 'condensed';
    const language = useSelector(getLanguage);
    const translate = useTranslate();
    const Tracking = useTracking();
    const previousBatter = usePrevious(batter, playerMatcher);
    const previousPitcher = usePrevious(pitcher, playerMatcher);
    const onClick = useCallback((e) => {
        const text = e?.target.innerText;
        const playerId = e?.currentTarget?.dataset?.playerid;
        const playerUrl = e?.currentTarget?.dataset?.url;
        const activity = 'Player Click';
        const cfg = {
            componentName: ['Live', 'Match Up'],
            text,
            activity,
            linkName: `${activity}: ${playerId}`
        };

        Tracking.track.track_click(cfg);

        if (deviceInteraction.playercard({ title: 'Player Card', url: playerUrl, pid: playerId })) {
            e.preventDefault();
        }
    }, [Tracking]);

    let matchups = [];

    const dictMap = {
        avg: translate('labelBrief_h_avg', 'avg'),
        hr: translate('labelBrief_h_hr', 'hr'),
        bb: translate('labelBrief_h_bb', 'bb'),
        slg: translate('labelBrief_h_slg', 'slg'),
        ip: translate('labelBrief_ip', 'ip'),
        p: translate('labelBrief_p', 'p'),
        strikes: translate('labelBrief_p_s', 'strikes'),
        era: translate('labelBrief_era', 'era'),
        pitcherK: translate('labelBrief_p_k', 'k'),
        whip: translate('labelBrief_p_whip', 'whip'),
        saves: translate('save_abbrev', 'sv'),
        blownSaves: translate('labelBrief_bs', 'bs'),
        holds: translate('labelBrief_hld', 'hld'),
        strikeOuts: translate('labelBrief_h_k', 'k'),
        numberOfPitches: translate('labelBrief_p_p', 'p'),
        er: translate('labelBrief_er', 'er'),
        '3b': translate('labelBrief_t', '3b'),
        '2b': translate('labelBrief_d', '2b'),
        cs: translate('labelBrief_h_cs', 'cs'),
        runs: translate('labelBrief_h_r', 'r'),
        sb: translate('labelBrief_h_sb', 'sb'),
        hbp: translate('labelBrief_hbp', 'hbp'),
        rbi: translate('labelBrief_rbi', 'rbi'),
        wins: translate('win_abbrev:', 'w'),
        losses: translate('loss_abbrev:', 'l'),
        lhp: translate('probables_lhp', 'LHP'),
        rhp: translate('probables_rhp', 'RHP')
    };

    const gameCodeOverride = gameStateOverride || abstractGameCode;

    // if Preview game state
    if (gameCodeOverride === 'P') {
        const probables = [];
        probables.push(probablePitchers?.away || null);
        probables.push(probablePitchers?.home || null);

        if (typeof headshotWidth !== 'undefined') {
            basePreviewProps = { ...basePreviewProps, headshotWidth };
        }

        matchups = probables.map((player, i) => {
            const {
                person: {
                    id
                } = {}
            } = player || {};

            if (!player) {
                return (
                    <PlayerMatchup
                        { ...basePreviewProps }
                        key={ i }
                        noNameLabel={ noNameLabel }
                        stacked={stacked}
                    />
                );
            } else {
                const {
                    person: {
                        boxscoreName,
                        pitchHand = {}
                    } = {},
                    seasonStats = {},
                    jerseyNumber
                } = player;

                const isPitcher = true;
                const playerStats = condensedDisplay
                    ? <>
                        {buildPlayerStatsDuel(condensedPitcherStats, probablePitchersStatsMap, seasonStats, dictMap, isPitcher)}
                    </>
                    : <>
                        <ProbablePitcherInfo pitchhand={pitchHand.code} jerseyNumber={jerseyNumber} key={`player-id-${id}`}/>
                        {buildPlayerStatsDuel(desiredStats, probablePitchersStatsMap, seasonStats, dictMap, isPitcher)}
                    </>
                ;

                return (
                    <>
                        <PlayerMatchupWrapper
                            { ...basePreviewProps }
                            playerId={ id }
                            name={ boxscoreName }
                            key={ i }
                            playerStats={ playerStats }
                            language={ language }
                            linkProps={{
                                handlers: { onClick }
                            }}
                            className={`${condensedDisplay ? 'condensed' : i % 2 === 0 ? 'left' : 'right'}`}
                            headshotZoomScale={ 1 }
                            $type={viewType}
                            pitchHand={condensedDisplay && pitchHand.code}
                        />
                    </>
                );
            }
        });

    // if Live game state
    } else {
        const desiredPitchingStats = {
            before: !isMobile && !isFullNarrow ? ['numberOfPitches', 'strikes'] : ['numberOfPitches'],
            after: ['ip', 'pitcherK', 'earnedRuns']
        };

        let players = [pitcher, batter];
        // The linescore will have the next players up, but we might want the current ones.
        if (usePreviousPlayers && previousPitcher && previousBatter) {
            players = [previousPitcher, previousBatter];
        }
        // this applies to some of the logic on the PlayerMatchup props below so need to confirm

        matchups = players.map((player = {}, i) => {
            const isPitcher = i === 0;
            const { person: { id, boxscoreName, pitchHand = {} } = {}, position = {}, stats } = player;
            const statsConfig = isPitcher ? desiredPitchingStats : desiredHittingStats;
            const statsMap = isPitcher ? statsMapsDuel.pitchersStatsMap : statsMapsDuel.hittersStatsMap;
            const requiresBreak = !isMobile && !stacked && !isFullNarrow; // breaking stats onto 2 lines if not on mobile.
            const playerStats = buildPlayerStatsDuel(statsConfig, statsMap, stats, dictMap, isPitcher, requiresBreak);
            const handPitchCode = pitchHand?.code ? `${pitchHand.code}HP`.toLowerCase() : '';

            return (
                <PlayerMatchupWrapper
                    type={ isPitcher ? PlayerMatchupTypes.PITCHERLIVE : PlayerMatchupTypes.HITTERLIVE }
                    playerId={ id }
                    key={ i }
                    stacked={stacked}
                    format={ PlayerMatchupFormats.VISUAL }
                    headshotWidth={ headshotWidth }
                    language={ language }
                    customPlayerInfo={ showCustomPlayerInfo ? (isPitcher ? `${dictMap?.[handPitchCode]}` : position.abbreviation) : null}
                    linkProps={{
                        handlers: { onClick }
                    }}
                    className={`${i % 2 === 0 ? 'left' : 'right'}`}
                    showHeadShot={true}
                    playerStats={playerStats}
                    name={boxscoreName}
                    headshotZoomScale={ 1 }
                    alignment={`${i % 2 === 0 ? 'left' : 'right'}`}
                    $type={viewType}
                />
            );
        });
    }

    return matchups;
};

MatchupsList.propTypes = {
    probablePitchers: PropTypes.object,
    abstractGameCode: PropTypes.string,
    batter: PropTypes.object,
    pitcher: PropTypes.object,
    gameStateOverride: PropTypes.string,
    stacked: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    usePreviousPlayers: PropTypes.bool,
    viewType: PropTypes.string,
    isMobile: PropTypes.bool,
    isFullNarrow: PropTypes.bool,
    showCustomPlayerInfo: PropTypes.bool
};

export default MatchupsList;
